import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Wrench, Save, Plus, X, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function AdminMaintenance() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBypass, setNewBypass] = useState("");
  const [newManager, setNewManager] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/maintenance`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_maintenance:load_failed", { defaultValue: "Failed to load" }));
    } finally {
      setLoading(false);
    }
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/maintenance`, patch);
      // server returns full doc
      setData({ ...data, ...res.data, is_super_admin: data?.is_super_admin });
      toast.success(t("admin_maintenance:saved", { defaultValue: "Saved" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_maintenance:save_failed", { defaultValue: "Save failed" }));
    } finally {
      setSaving(false);
    }
  };

  const onChange = (field, value) => setData((d) => ({ ...d, [field]: value }));

  const addBypass = async () => {
    const email = newBypass.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    const list = Array.from(new Set([...(data.bypass_emails || []), email]));
    await save({ bypass_emails: list });
    setNewBypass("");
  };
  const removeBypass = async (email) => {
    const list = (data.bypass_emails || []).filter((e) => e !== email);
    await save({ bypass_emails: list });
  };
  const addManager = async () => {
    const email = newManager.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    const list = Array.from(new Set([...(data.manager_emails || []), email]));
    await save({ manager_emails: list });
    setNewManager("");
  };
  const removeManager = async (email) => {
    const list = (data.manager_emails || []).filter((e) => e !== email);
    await save({ manager_emails: list });
  };

  if (loading || !data) {
    return <div className="animate-pulse bg-muted h-96 rounded-xl" />;
  }

  const isSuperAdmin = !!data.is_super_admin;

  return (
    <div data-testid="admin-maintenance-page" className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <Wrench className="h-6 w-6 text-primary" />
          {t("admin_maintenance:title", { defaultValue: "Maintenance Mode" })}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t("admin_maintenance:subtitle", {
            defaultValue: "When enabled, visitors see a maintenance screen. Only emails on the bypass list can still view the site.",
          })}
        </p>
      </div>

      {/* Enable toggle */}
      <div
        className={`rounded-xl border p-5 mb-6 flex items-center justify-between ${
          data.is_enabled
            ? "bg-amber-50 border-amber-300"
            : "bg-card border-border"
        }`}
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className={`h-5 w-5 ${data.is_enabled ? "text-amber-600" : "text-muted-foreground"}`} />
          <div>
            <p className="font-medium text-foreground">
              {data.is_enabled
                ? t("admin_maintenance:status_on", { defaultValue: "Maintenance is ACTIVE" })
                : t("admin_maintenance:status_off", { defaultValue: "Maintenance is off" })}
            </p>
            <p className="text-xs text-muted-foreground">
              {data.is_enabled
                ? t("admin_maintenance:status_on_help", { defaultValue: "Only bypass-listed emails can access the site." })
                : t("admin_maintenance:status_off_help", { defaultValue: "Site is public for everyone." })}
            </p>
          </div>
        </div>
        <Switch
          checked={data.is_enabled}
          onCheckedChange={(v) => save({ is_enabled: v })}
          disabled={saving}
          data-testid="maintenance-enabled-switch"
        />
      </div>

      {/* Content block */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6 space-y-4">
        <h2 className="font-medium text-foreground">
          {t("admin_maintenance:content", { defaultValue: "Modal Content" })}
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t("admin_maintenance:title_en", { defaultValue: "Title (EN)" })}</Label>
            <Input
              value={data.title || ""}
              onChange={(e) => onChange("title", e.target.value)}
              className="mt-1"
              data-testid="maintenance-title-en"
            />
          </div>
          <div>
            <Label>{t("admin_maintenance:title_el", { defaultValue: "Τίτλος (EL)" })}</Label>
            <Input
              value={data.title_el || ""}
              onChange={(e) => onChange("title_el", e.target.value)}
              className="mt-1"
              data-testid="maintenance-title-el"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t("admin_maintenance:message_en", { defaultValue: "Message (EN)" })}</Label>
            <Textarea
              value={data.message || ""}
              onChange={(e) => onChange("message", e.target.value)}
              className="mt-1 min-h-[90px]"
              data-testid="maintenance-message-en"
            />
          </div>
          <div>
            <Label>{t("admin_maintenance:message_el", { defaultValue: "Μήνυμα (EL)" })}</Label>
            <Textarea
              value={data.message_el || ""}
              onChange={(e) => onChange("message_el", e.target.value)}
              className="mt-1 min-h-[90px]"
              data-testid="maintenance-message-el"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t("admin_maintenance:contact_email", { defaultValue: "Contact Email" })}</Label>
            <Input
              type="email"
              value={data.contact_email || ""}
              onChange={(e) => onChange("contact_email", e.target.value)}
              className="mt-1"
              data-testid="maintenance-contact-email"
            />
          </div>
          <div>
            <Label>{t("admin_maintenance:logo_url", { defaultValue: "Logo URL (optional)" })}</Label>
            <Input
              value={data.logo_url || ""}
              onChange={(e) => onChange("logo_url", e.target.value)}
              className="mt-1"
              placeholder="https://..."
              data-testid="maintenance-logo-url"
            />
          </div>
        </div>

        <div>
          <Label>
            {t("admin_maintenance:end_time", { defaultValue: "Back-online Time (optional) — shows a countdown" })}
          </Label>
          <Input
            type="datetime-local"
            value={data.end_time ? data.end_time.slice(0, 16) : ""}
            onChange={(e) => onChange("end_time", e.target.value ? new Date(e.target.value).toISOString() : "")}
            className="mt-1"
            data-testid="maintenance-end-time"
          />
        </div>

        <Button
          onClick={() =>
            save({
              title: data.title,
              title_el: data.title_el,
              message: data.message,
              message_el: data.message_el,
              contact_email: data.contact_email,
              logo_url: data.logo_url,
              end_time: data.end_time || "",
            })
          }
          disabled={saving}
          data-testid="maintenance-save-content-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? t("common:saving", { defaultValue: "Saving..." }) : t("admin_maintenance:save_content", { defaultValue: "Save Content" })}
        </Button>
      </div>

      {/* Bypass emails — SUPER ADMIN ONLY */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div>
            <h2 className="font-medium text-foreground">
              {t("admin_maintenance:bypass_title", { defaultValue: "Bypass Emails" })}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("admin_maintenance:bypass_help", {
                defaultValue:
                  "These emails can still access the site during maintenance. Super-admins ALWAYS have access even if not listed.",
              })}
            </p>
          </div>
        </div>

        {!isSuperAdmin && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            {t("admin_maintenance:super_only", { defaultValue: "Only super-admins can edit this list." })}
          </p>
        )}

        <div className="space-y-2 mb-3">
          {(data.bypass_emails || []).map((e) => (
            <div
              key={e}
              className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2"
              data-testid={`bypass-email-${e}`}
            >
              <span className="text-sm font-mono">{e}</span>
              {isSuperAdmin && (
                <button
                  onClick={() => removeBypass(e)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="remove"
                  data-testid={`bypass-remove-${e}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {!(data.bypass_emails || []).length && (
            <p className="text-xs text-muted-foreground italic">
              {t("admin_maintenance:no_emails", { defaultValue: "No emails added." })}
            </p>
          )}
        </div>

        {isSuperAdmin && (
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="admin@example.com"
              value={newBypass}
              onChange={(e) => setNewBypass(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addBypass())}
              data-testid="bypass-email-input"
            />
            <Button onClick={addBypass} data-testid="bypass-add-btn">
              <Plus className="h-4 w-4 mr-1" /> {t("admin_maintenance:add", { defaultValue: "Add" })}
            </Button>
          </div>
        )}
      </div>

      {/* Manager emails — SUPER ADMIN ONLY */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div>
            <h2 className="font-medium text-foreground">
              {t("admin_maintenance:managers_title", { defaultValue: "Maintenance Managers" })}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("admin_maintenance:managers_help", {
                defaultValue:
                  "Non-super-admin emails listed here can toggle & edit maintenance (but cannot edit these lists).",
              })}
            </p>
          </div>
        </div>

        {!isSuperAdmin && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            {t("admin_maintenance:super_only", { defaultValue: "Only super-admins can edit this list." })}
          </p>
        )}

        <div className="space-y-2 mb-3">
          {(data.manager_emails || []).map((e) => (
            <div
              key={e}
              className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2"
              data-testid={`manager-email-${e}`}
            >
              <span className="text-sm font-mono">{e}</span>
              {isSuperAdmin && (
                <button
                  onClick={() => removeManager(e)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="remove"
                  data-testid={`manager-remove-${e}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {!(data.manager_emails || []).length && (
            <p className="text-xs text-muted-foreground italic">
              {t("admin_maintenance:no_emails", { defaultValue: "No emails added." })}
            </p>
          )}
        </div>

        {isSuperAdmin && (
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="admin@example.com"
              value={newManager}
              onChange={(e) => setNewManager(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addManager())}
              data-testid="manager-email-input"
            />
            <Button onClick={addManager} data-testid="manager-add-btn">
              <Plus className="h-4 w-4 mr-1" /> {t("admin_maintenance:add", { defaultValue: "Add" })}
            </Button>
          </div>
        )}
      </div>

      {data.updated_at && (
        <p className="text-xs text-muted-foreground">
          {t("admin_maintenance:updated_at", { defaultValue: "Last updated" })}: {new Date(data.updated_at).toLocaleString()}
          {data.updated_by ? ` · ${data.updated_by}` : ""}
        </p>
      )}
    </div>
  );
}
